import constants from '@Common/Core/Constant';
import { DataServiceProvider } from '@Common/Core/DataServiceProvider';
import { FunctionType, OperatorType, TypeKind } from '@Common/Enums/Enum';
import * as commonUtils from '@Common/Helpers/CommonUtils';
import {
    DataSourceWSComplexField,
    DBMapping,
    DBMappingDataSource,
    DBMappingEmbededDataSource,
    DBMappingSource,
    DBMappingSource_RecordDocument,
    DBMappingSource_TranslationTable,
    DBMappingSourceBase,
    DerivedField,
    MappingSourceLink,
    MathExpression,
    MetricPool,
    RecordDocument,
    SpecialAggregationFunctionOperand,
    SQLFunctionDefinition,
    SQLFunctionOperand
} from '@InstanceFactory';
import statementsFactory from '@Studio/StatementsFactory';
import { TraverseStatement } from '@Studio/TraverseStatement';
import {
    FieldsInStatementCollector,
    RemoveFieldStatementVisitor,
    ReplaceAliasInStatement,
    ReplaceFieldIdInStatement
} from './../../StatementVisitor';
import { enumerable } from '@Common/Core/CoreDecorator';

namespace dbMapperControlHelper {
    //#region Sources Map

    export const derivedFieldType = 'derivedField' as 'derivedField';

    export interface IFieldInfo {
        id: string;
        isDerivedField: boolean;
    }

    export interface IFieldsMap {
        alias: string;
        source: any;

        getField(fieldId: string): IFieldInfo;

        allFields(): IFieldInfo[];
    }

    export interface ISourcesMap {
        getSource(alias: string): IFieldsMap;

        allSources(): IFieldsMap[];
    }

    export class SourcesMap {
        private _sources: _.Dictionary<FieldsMap>;

        constructor(dbMapping: DBMapping) {
            const allSources = cascade(dbMapping.rootSources, s => s.joinedSources);
            this._sources = _(allSources)
                .map(s => new FieldsMap(s))
                .keyBy(fm => fm.source.alias)
                .value();
        }

        public getSource(alias: string): IFieldsMap {
            return this._sources[alias] || null;
        }

        public allSources(): IFieldsMap[] {
            return _.values(this._sources);
        }
    }

    class FieldsMap implements IFieldsMap {
        private _source: DBMappingSourceBase;
        private _fields: _.Dictionary<FieldInfo>;

        constructor(source: DBMappingSourceBase) {
            this._source = source;
            this._fields = _(getSourceDocumentFields(this._source))
                .union(this._source.derivedFields)
                .map(f => new FieldInfo(f))
                .keyBy(fi => fi.id)
                .value();
        }

        @enumerable
        public get alias(): string {
            return this._source.alias;
        }

        @enumerable
        public get source() {
            return this._source;
        }

        public getField(fieldId: string): IFieldInfo {
            return this._fields[fieldId] || null;
        }

        public allFields(): IFieldInfo[] {
            return _.values(this._fields);
        }
    }

    class FieldInfo implements IFieldInfo {
        private _field;

        constructor(field) {
            this._field = field;
        }

        @enumerable
        public get id(): string {
            return fieldId(this._field);
        }

        @enumerable
        public get isDerivedField(): boolean {
            return this._field === derivedFieldType;
        }
    }

    //#endregion

    export function getSourceDocumentFields(source: DBMappingSourceBase) {
        if (!source.sourceDocument.document) {
            return [];
        }
        const items: any[] = source.sourceDocument.document.items;
        return cascade(items, i => {
            if (i.$type === DataSourceWSComplexField.typeName) {
                return i.parameters;
            } else {
                return [];
            }
        }).filter(i => i.$type !== DataSourceWSComplexField.typeName);
    }

    export interface IItemIMappingInfo {
        field;
        link: MappingSourceLink;
        table;
        source;
    }

    export function findMappedElement(dbMapping: DBMapping, dimensionId: string): IItemIMappingInfo[] {
        const result = [];
        cascade(dbMapping.rootSources, s => s.joinedSources).forEach(source => {
            _(source.links)
                .filter(link => link.targetItem === dimensionId)
                .forEach(link => {
                    if (!source.sourceDocument.document) {
                        throw new Error('Reference document is not loaded for source ' + source.alias);
                    }

                    const table = source.sourceDocument.document;
                    _(table.items)
                        .filter(item => item.id === link.sourceField)
                        .forEach(item => {
                            result.push({
                                field: item,
                                link: link,
                                table: table,
                                source: source
                            });
                        });
                    _(source.derivedFields)
                        .filter(derivedField => derivedField.name === link.sourceField)
                        .forEach(derivedField => {
                            result.push({
                                field: derivedField,
                                link: link,
                                table: table,
                                source: source
                            });
                        });
                });
        });
        return result;
    }

    export interface IParentSourceInfo {
        parent: IParentSourceInfo;
        source;
    }

    export function cascade<T>(items: T[], getItems: (item: T) => T[]): T[] {
        let result: T[] = [];
        _.forEach(items, item => {
            result.push(item);
            result = _.union(result, cascade(getItems(item), getItems));
        });
        return result;
    }

    export function getParentSourceList(dbMapping: DBMapping): IParentSourceInfo[] {
        const parentSourceList = _.map(dbMapping.rootSources, s => {
            return { parent: null, source: s };
        });
        return cascade(parentSourceList, i =>
            _.map(i.source.joinedSources, s => {
                return { parent: i, source: s };
            })
        );
    }

    function getParentSourceInfo(dbMapping: DBMapping, sourceAlias: string): IParentSourceInfo {
        return _.find(getParentSourceList(dbMapping), info => info.source.alias === sourceAlias);
    }

    export function getParentSource(dbMapping: DBMapping, sourceAlias: string): DBMappingSourceBase {
        const sourceInfo = getParentSourceInfo(dbMapping, sourceAlias);
        if (sourceInfo && sourceInfo.parent) {
            return sourceInfo.parent.source;
        } else {
            return null;
        }
    }

    export function getRootSource(dbMapping: DBMapping, sourceAlias: string): DBMappingSourceBase {
        let sourceInfo = getParentSourceInfo(dbMapping, sourceAlias);
        if (sourceInfo) {
            while (sourceInfo.parent) {
                sourceInfo = sourceInfo.parent;
            }
            return sourceInfo.source;
        } else {
            return null;
        }
    }

    export function getRootDataSource(source, projectId, dataServiceProvider: DataServiceProvider, dbMapping: DBMapping): any {
        let rootSource = dbMapping.rootSources[0];
        const sourceDeferred = $.Deferred();
        if (isRecordSource(source)) {
            dataServiceProvider
                .loadDocument(projectId, rootSource.sourceDocument.id, rootSource.sourceDocument.name, [constants.studioDocumentTypes.record])
                .done((doc: RecordDocument) => {
                    rootSource = doc.dbMapping.rootSources[0];
                    if (isRecordSource(rootSource)) {
                        getRootDataSource(rootSource, projectId, dataServiceProvider, doc.dbMapping).done(s => {
                            sourceDeferred.resolve(s);
                        });
                    } else {
                        sourceDeferred.resolve(rootSource);
                    }
                })
                .fail(() => {
                    console.log('Can not load Data Source. Use default database type');
                    sourceDeferred.resolve(null);
                });
        } else {
            sourceDeferred.resolve(source);
        }
        return sourceDeferred.promise();
    }

    export function getField(dbMapping: DBMapping, sourceAlias: string, fieldId: string): { source; field } {
        const source = _.find(
            cascade(dbMapping.rootSources, s => s.joinedSources),
            s => s.alias === sourceAlias
        );
        if (!source) {
            return null;
        }

        const derivedField = _.find(source.derivedFields, f => f.name === fieldId);
        if (derivedField) {
            return { source: source, field: derivedField };
        }

        const field = _.find(source.sourceDocument.document.items, (f: any) => f.id === fieldId);
        if (field) {
            return { source: source, field: field };
        } else {
            return null;
        }
    }

    export function removeSource(dbMapping: DBMapping, sourceAlias: string): boolean {
        const sourceInfo = getParentSourceInfo(dbMapping, sourceAlias);
        if (sourceInfo) {
            if (sourceInfo.parent) {
                sourceInfo.parent.source.joinedSources.remove(sourceInfo.source);
            } else {
                dbMapping.rootSources.remove(sourceInfo.source);
            }

            const removedAliases = cascade([sourceInfo.source], s => s.joinedSources).map(s => s.alias);

            _(cascade(dbMapping.rootSources, s => s.joinedSources))
                .flatMap(s => getSourceStatements(s))
                .forEach(s => {
                    removedAliases.forEach(alias => {
                        const visitor = new RemoveFieldStatementVisitor(undefined /* remove all fields from source*/, alias);
                        visitor.accept(s.statement);
                    });
                });
            return true;
        } else {
            return false;
        }
    }

    export function setDefaultTimeFilter(source: DBMappingSourceBase, timeOperands: any[]) {
        const mathExpression = statementsFactory.createMathExpression();
        if (!!timeOperands && timeOperands.length > 0) {
            const fieldOperand = timeOperands[0];
            mathExpression.expressionElements = [
                fieldOperand,
                statementsFactory.createOperator(OperatorType.greaterOrEqual),
                statementsFactory.createCustomQueryParameter('FromDate', false),
                statementsFactory.createOperator(OperatorType.and),
                commonUtils.clone(fieldOperand),
                statementsFactory.createOperator(OperatorType.lessThan),
                statementsFactory.createCustomQueryParameter('ToDate', false)
            ];
        }
        source.timeFilter = mathExpression;
    }

    export function removeAllLinksForDimension(dbMapping: DBMapping, dimensionId: string) {
        cascade(dbMapping.rootSources, s => s.joinedSources).forEach(source => {
            source.links = _.filter(source.links, link => link.targetItem !== dimensionId);
        });
    }

    export function isExternalJoin(source, parentSource): boolean {
        if (!parentSource) {
            return false;
        }

        return isDBTableSource(source) && isDBTableSource(parentSource) && source.dataSource.id !== parentSource.dataSource.id;
    }

    export function isRecordOnRecord(dbMapping: DBMapping): boolean {
        return _(cascade(dbMapping.rootSources, s => s.joinedSources)).some(s => !isDBTableSource(s) && !isEmbededFileSource(s));
    }

    export function isRecordSource(dbMappingSource): dbMappingSource is DBMappingSource<RecordDocument> {
        return dbMappingSource.$type === DBMappingSource_RecordDocument.typeName;
    }

    export function isMetricSource(dbMappingSourceTable): dbMappingSourceTable is MetricPool {
        return MetricPool.isInstanceOfType(dbMappingSourceTable);
    }

    export function isDBTableSource(dbMappingSource): dbMappingSource is DBMappingDataSource {
        return dbMappingSource.$type === DBMappingDataSource.typeName;
    }

    export function isEmbededFileSource(dbMappingSource): dbMappingSource is DBMappingEmbededDataSource {
        return dbMappingSource.$type === DBMappingEmbededDataSource.typeName;
    }

    export function changeTableAlias(dbMapping: DBMapping | DBMappingSourceBase[], oldAlias: string, newAlias: string) {
        if (oldAlias === newAlias) {
            return;
        }

        let sources: DBMappingSourceBase[];
        if (Array.isArray(dbMapping)) {
            sources = dbMapping;
        } else {
            sources = dbMapping.rootSources;
            if (dbMapping.queryDescription) {
                _.forEach(dbMapping.queryDescription.partDescriptions, part => {
                    if (part.groupId === oldAlias) {
                        part.groupId = newAlias;
                    }
                });
            }
        }

        _(cascade(sources, s => s.joinedSources))
            .flatMap(s => getSourceStatements(s))
            .forEach(s => {
                const visitor = new ReplaceAliasInStatement(oldAlias, newAlias);
                visitor.accept(s.statement);
            });
    }

    export function changeSourceFieldId(dbMapping: DBMapping, oldFieldId: string, newFieldId: string, tableAlias: string) {
        if (oldFieldId === newFieldId) {
            return;
        }

        _(cascade(dbMapping.rootSources, s => s.joinedSources))
            .filter(s => s.alias === tableAlias)
            .flatMap(s => s.links)
            .filter(l => l.sourceField === oldFieldId)
            .forEach(l => {
                l.sourceField = newFieldId;
            });

        _(cascade(dbMapping.rootSources, s => s.joinedSources))
            .flatMap(s => getSourceStatements(s))
            .forEach(s => {
                const visitor = new ReplaceFieldIdInStatement(oldFieldId, newFieldId, tableAlias);
                visitor.accept(s.statement);
            });
    }

    export function getSourceStatements(source): { statement: MathExpression; part: string }[] {
        const statements = [];
        if (!!source.joinCondition) {
            statements.push({
                statement: source.joinCondition,
                part: 'Join Condition'
            });
        }
        if (!!source.timeFilter) {
            statements.push({
                statement: source.timeFilter,
                part: 'Time Filter'
            });
        }
        if (!!source.dataFilter) {
            statements.push({
                statement: source.dataFilter,
                part: 'Data Filter'
            });
        }
        statements.addRange(
            _(source.derivedFields)
                .filter(df => !!df.transformationExpression)
                .map(df => {
                    return {
                        statement: df.transformationExpression,
                        part: "Derived field '" + df.name + "'"
                    };
                })
                .value()
        );
        return statements;
    }

    export function fieldId(field): string {
        return field.id || field.name;
    }

    export function fieldDisplayName(field): string {
        return field.displayName || field.id || field.name;
    }

    export function sourceFieldMapping(source: DBMappingSourceBase, field: string): string {
        const link = _.find(source.links, l => l.sourceField === field);
        return link && link.targetItem;
    }

    export function getUsedFieldsFromSource(source: DBMappingSourceBase, dbMapping: DBMapping): any[] {
        const derivedFields = _.map(source.derivedFields, df => df.name);

        const mappedFields = _(source.links)
            .map(l => l.sourceField)
            .difference(derivedFields)
            .value();
        const fieldsInStatements = _(cascade(dbMapping.rootSources, s => s.joinedSources))
            .flatMap(s => getSourceStatements(s))
            .flatMap(expr => {
                const collector = new FieldsInStatementCollector();
                collector.accept(expr.statement);
                return collector.getFields();
            })
            .filter(f => f.tableAlias === source.alias)
            .map(f => f.fieldId)
            .difference(derivedFields)
            .value();

        return _.union(mappedFields, fieldsInStatements);
    }

    export function getTransformation(source: DBMappingSourceBase, fieldId: string): string {
        const link = _.find(source.links, l => l.sourceField === fieldId);
        return link && link.transformation;
    }

    //#region Aggregations

    export function getAggregationForDimension(dbMapping: DBMapping, itemId): string {
        const mapInfos = findMappedElement(dbMapping, itemId);
        if (mapInfos && mapInfos.length > 0) {
            return getAggregationForField(mapInfos[0]);
        } else {
            return null;
        }
    }

    function getStatementAggregation(statement): string {
        if (statement.$type !== MathExpression.typeName || statement.expressionElements.length === 0) {
            return null;
        }
        if (
            statement.expressionElements[0].$type === SQLFunctionOperand.typeName ||
            statement.expressionElements[0].$type === SpecialAggregationFunctionOperand.typeName
        ) {
            return statement.expressionElements[0].name;
        }
        return null;
    }

    function convertTransformToSql(functionName: string): string {
        switch (functionName) {
            case 'COUNT UNIQUE':
                return constants.aggregationFunctions.countUnique;
            default:
                return functionName;
        }
    }

    export function filterAggregationFunction(functionName: string): string {
        functionName = convertTransformToSql(functionName);
        if (functionName === constants.percentileFunction || functionName === constants.medianFunction) {
            return functionName;
        }
        const aggregationFunctions = constants.aggregationFunctions;
        const isAggr = _(aggregationFunctions)
            .keys()
            .some(key => aggregationFunctions[key] === functionName);
        return isAggr ? functionName : null;
    }

    function getAggregationForField(mappingInfo: IItemIMappingInfo): string {
        const field = mappingInfo.field;
        //let fieldId = field.id || field.name;
        const transformation = mappingInfo.link.transformation;
        if (transformation) {
            const sqlFunction = convertTransformToSql(transformation);
            return filterAggregationFunction(sqlFunction);
        }

        if (field.$type === DerivedField.typeName) {
            const statementAggregation = getStatementAggregation(field.transformationExpression);
            return filterAggregationFunction(statementAggregation);
        }

        return null;
    }

    //#endregion

    export function updateDescriptorAttributes(descriptor, document) {
        if (!descriptor.attributes) {
            descriptor.attributes = [];
        }
        const attrs: { name: string; value: string }[] = descriptor.attributes;

        updateOrCreateAttr(attrs, 'RecordVirtual', (!document.storageSettings).toString());
        updateOrCreateAttr(attrs, 'RecordType', dbMapperControlHelper.isRecordOnRecord(document.dbMapping) ? 'recordDerived' : 'recordDirect');
    }

    function updateOrCreateAttr(attrCollection: { name: string; value: string }[], attrName: string, attrValue: string) {
        let attr: { name: string; value: string } = attrCollection.filter(a => a.name === attrName)[0];
        if (attr == null) {
            attrCollection.push((attr = { name: attrName, value: attrValue }));
        }
        attr.value = attrValue;
    }

    export function getSourceImage(source): string {
        switch (source.$type) {
            case DBMappingDataSource.typeName:
                return 'Documents/database table.png';
            case DBMappingSource_RecordDocument.typeName:
                return 'Documents/record group.png';
            case DBMappingSource_TranslationTable.typeName:
                return 'Documents/translation table.png';
            case DBMappingEmbededDataSource.typeName:
                return 'Documents/file source.png';
            default:
                return 'Documents/basic metric.png';
        }
    }

    export function referencesLoadedInSource(source): { reference: any; part: string } {
        if (!source.sourceDocument.document) {
            return {
                part: 'Source',
                reference: source.sourceDocument
            };
        }
        if (isDBTableSource(source) && !source.dataSource.document) {
            return {
                part: 'Data source',
                reference: source.dataSource
            };
        }
        return null;
    }

    export function getAllSources(dbMapping: DBMapping): DBMappingSourceBase[] {
        return cascade(dbMapping.rootSources, s => s.joinedSources);
    }

    export function getAllDerivedFields(dbMapping: DBMapping): DerivedField[] {
        const allSources = getAllSources(dbMapping);
        return _.flatMap(allSources, source => source.derivedFields);
    }

    export function mappingHasPossibleLossOfData(
        sourceField: { type: TypeKind; length: number },
        targetField: { type: TypeKind; length: number },
        link: MappingSourceLink
    ): boolean {
        if (!link.transformation) {
            switch (sourceField.type) {
                case TypeKind.string:
                    return (
                        targetField.type === TypeKind.float ||
                        targetField.type === TypeKind.int ||
                        (targetField.type === TypeKind.string && targetField.length < sourceField.length)
                    );
                case TypeKind.float:
                    return targetField.type === TypeKind.int;
            }
        }

        return false;
    }

    export function hasAggregation(expression: MathExpression, sqlFunctions: SQLFunctionDefinition[] = altosoft.model.sqlFunctions): boolean {
        const aggFunctions = sqlFunctions.filter(fn => fn.functionType === FunctionType.aggregative).map(fn => _.toUpper(fn.name));
        const functionsSet = new Set(aggFunctions);
        let res = false;
        new TraverseStatement({
            visitSqlFunction(fn: SQLFunctionOperand): void {
                res = res || functionsSet.has(_.toUpper(fn.name));
            },
            visitSpecialAggregationFunction(fn: SpecialAggregationFunctionOperand): void {
                res = true;
            }
        }).accept(expression);
        return res;
    }
}
export default dbMapperControlHelper;
